import * as THREE from 'three';
import { settings } from '@/config';

export interface WaterSimpleOptions {
  normalMap0?: THREE.Texture;
  normalMap1?: THREE.Texture;
}

/**
 * DEDICATED LOW-END WATER (2026-09-04, user: "we have to recreate water shaders specifically
 * for low end devices — still it's broken in phones. Keep this one [the complex reflective
 * water] for when we detect a GPU or a nice CPU; if someone has a low-end device, serve them
 * that optimized water shader").
 *
 * A fully standalone surface for phones, software WebGL and weak machines — NOT a mode of
 * `WaterComplex` but its own tiny mesh + shader, chosen ONCE at boot by `WaterFX` (see the
 * device gate there). On a low-end device the complex class is never even constructed, so
 * none of its machinery exists to fail: no planar `Reflector`, no nested scene re-renders,
 * no reflection render targets, no `onBeforeRender` hooks, no logdepth chunks, no FX
 * sampler, and no high-power `pow()` (the complex shader's shininess-260 specular is a
 * classic mobile-GPU NaN source — here the glint is ^32 by repeated squaring).
 *
 * What it keeps of the real water's character, per the user's "exactly the same properties":
 * two scrolling normal maps for the ripple, a cubic Fresnel deep→sky blend, a FAKE SKY
 * REFLECTION (the reflected ray's Y mixes the sky gradient's horizon light into its zenith
 * colour — the mirror-of-sky read without mirroring anything), a warm sun glint, and the
 * scene fog. Per-frame cost: one `uTime` float tick from `WaterFX.update`.
 */
export class WaterSimple extends THREE.Mesh {
  declare material: THREE.ShaderMaterial;

  constructor(geometry: THREE.BufferGeometry, options: WaterSimpleOptions = {}) {
    super(geometry);

    const cfg = settings.water;
    const n0 = options.normalMap0 ?? null;
    const n1 = options.normalMap1 ?? null;
    // Standalone: set the wrap mode ourselves (WaterComplex normally does this).
    if (n0) n0.wrapS = n0.wrapT = THREE.RepeatWrapping;
    if (n1) n1.wrapS = n1.wrapT = THREE.RepeatWrapping;

    this.material = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib['fog'],
        {
          uTime: { value: 0 },
          tN0: { value: n0 },
          tN1: { value: n1 },
          uFlowDir: {
            value: new THREE.Vector2(cfg.flowDirection[0], cfg.flowDirection[1]).normalize(),
          },
          uScale: { value: cfg.scale },
          // Soft haze tint standing in for reflected architecture (same value the complex
          // shader uses as its no-reflection fallback).
          uShallow: { value: new THREE.Color(0xcfdde8) },
          uDeep: { value: new THREE.Color(cfg.deepColor) },
          uSunDir: {
            value: new THREE.Vector3(cfg.sunDirection[0], cfg.sunDirection[1], cfg.sunDirection[2]),
          },
          uSunColor: { value: new THREE.Color(cfg.sunColor) },
          // The fake sky mirror reads the real sky gradient, so the pool always matches
          // whatever sky the scene is actually wearing.
          uSkyHorizon: { value: new THREE.Color(settings.sky.horizon) },
          uSkyTop: { value: new THREE.Color(settings.sky.middle) },
          uTint: { value: new THREE.Color(cfg.color) },
        },
      ]),
      vertexShader: /* glsl */ `
        #include <common>
        #include <fog_pars_vertex>
        varying vec2 vUv;
        varying vec3 vToEye;
        void main() {
          vUv = uv;
          vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
          vToEye = cameraPosition - worldPosition.xyz;
          vec4 mvPosition = viewMatrix * worldPosition;
          gl_Position = projectionMatrix * mvPosition;
          #include <fog_vertex>
        }`,
      fragmentShader: /* glsl */ `
        #include <common>
        #include <fog_pars_fragment>
        uniform sampler2D tN0;
        uniform sampler2D tN1;
        uniform float uTime;
        uniform vec2 uFlowDir;
        uniform float uScale;
        uniform vec3 uShallow;
        uniform vec3 uDeep;
        uniform vec3 uSunDir;
        uniform vec3 uSunColor;
        uniform vec3 uSkyHorizon;
        uniform vec3 uSkyTop;
        uniform vec3 uTint;
        varying vec2 vUv;
        varying vec3 vToEye;
        void main() {
          vec2 drift = uFlowDir * uTime * 0.02;
          vec3 n0 = texture2D( tN0, vUv * uScale + drift ).rgb;
          vec3 n1 = texture2D( tN1, vUv * uScale * 1.63 - drift * 0.8 ).rgb;
          vec3 normal = normalize( vec3( n0.r + n1.r - 1.0, ( n0.b + n1.b ) * 1.6, n0.g + n1.g - 1.0 ) );
          vec3 toEye = normalize( vToEye );
          float theta = clamp( dot( toEye, normal ), 0.0, 1.0 );
          float f = 1.0 - theta;
          float fres = f * f * f; /* cubic fresnel - cheap, mediump-safe */
          /* Fake sky mirror: the reflected ray's Y blends horizon light into zenith blue - the
             pool reads as reflecting the sky, with the ripples visibly bending that mirror. */
          vec3 refl = reflect( -toEye, normal );
          vec3 sky = mix( uSkyHorizon, uSkyTop, clamp( refl.y * 1.6, 0.0, 1.0 ) );
          sky = mix( sky, uShallow, 0.35 ) * uTint;
          vec3 surface = mix( uDeep, sky, clamp( 0.18 + fres, 0.0, 1.0 ) );
          vec3 halfV = normalize( normalize( uSunDir ) + toEye );
          float s = clamp( dot( normal, halfV ), 0.0, 1.0 );
          s = s * s; s = s * s; s = s * s; s = s * s; s = s * s; /* ^32 by squaring - no pow */
          surface += uSunColor * s * 0.45;
          gl_FragColor = vec4( surface, 1.0 );
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
          #include <fog_fragment>
        }`,
      transparent: false,
      fog: true,
    });
  }

  /** Ticked by `WaterFX.update` — the whole per-frame cost of this water. */
  update(delta: number): void {
    (this.material.uniforms['uTime'] as THREE.IUniform<number>).value += delta;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
